<script setup>
import { ref } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";
const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;

const { t } = useI18n();

const company = ref({
  name: "",
  logo: null,
  founded: "",
  employees: "",
  about: { sv: "", en: "" },
  values: { sv: "", en: "" },
  locations: { sv: "", en: "" },
  partner: false,
  contactMeeting: false,
  availableTimes: [],
  tags: {
    sv: [],
    en: [],
  },
});

const logoFile = ref(null);

// Lista över tider (samma som i ditt schema)
const availableTimesEnum = [
  "10:00-10:25",
  "10:25-10:50",
  "10:50-11:15",
  "11:15-11:40",
  "11:40-12:05",
  "12:05-12:30",
  "12:30-12:55",
  "12:55-13:20",
  "13:20-13:45",
  "13:45-14:10",
  "14:10-14:35",
  "14:35-15:00",
];

const handleLogoUpload = (event) => {
  const file = event?.target?.files?.[0];
  if (!file || !(file instanceof Blob)) {
    console.warn("Ingen giltig fil hittades:", file);
    return;
  }

  logoFile.value = file;

  const reader = new FileReader();
  reader.onload = () => {
    company.value.logo = {
      url: reader.result,
      fileName: file.name,
      contentType: file.type,
      uploadedAt: new Date().toISOString(),
    };
  };
  reader.readAsDataURL(file);
};

const addCompany = async () => {
  try {
    const formData = new FormData();

    // Enkla textfält
    const textFields = [
      "name",
      "founded",
      "employees",
      "partner",
      "contactMeeting",
    ];
    textFields.forEach((field) => {
      // Gör null eller undefined till tom sträng om du vill
      formData.append(field, company.value[field] ?? "");
    });

    // Nested fält med språk
    const nestedFields = ["about", "values", "locations"];
    nestedFields.forEach((field) => {
      ["sv", "en"].forEach((lang) => {
        formData.append(`${field}.${lang}`, company.value[field]?.[lang] ?? "");
      });
    });

    // Arrayfält med språk (exempel: tags.sv, tags.en)
    const arrayFields = ["tags"];
    arrayFields.forEach((field) => {
      ["sv", "en"].forEach((lang) => {
        (company.value[field]?.[lang] || []).forEach((val, i) => {
          formData.append(`${field}.${lang}.${i}`, val);
        });
      });
    });

    // Arrayfält utan språk
    (company.value.availableTimes || []).forEach((time, i) => {
      formData.append(`availableTimes.${i}`, time);
    });

    // Logotyp (fil)
    if (logoFile.value) {
      formData.append("logo", logoFile.value);
    } else {
      // Om du vill skicka "ingen logotyp" kan du göra t.ex. formData.append("logo", "")
      // annars låt bli att skicka alls för att behålla befintlig
    }

    const response = await axios.post(`${apiBaseUrl}/companies`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    console.log("Företag skapat!", response.data);
    // återställ formulär eller navigera vidare
  } catch (error) {
    console.error("Fel vid skapande av företag:", error);
  }
};
</script>

<template>
  <v-container class="mt-16">
    <v-form @submit.prevent="addCompany">
      <!-- Namn -->
      <v-text-field
        v-model="company.name"
        :label="t('company.name')"
        required
        color="primary"
      />

      <!-- Logo (filuppladdning, bara metadata i detta exempel) -->
      <v-file-input
        label="Logo"
        accept="image/*"
        :multiple="false"
        @change="handleLogoUpload"
        color="primary"
      />

      <!-- Grundat år -->
      <v-text-field
        v-model="company.founded"
        :label="t('company.founded')"
        color="primary"
      />

      <!-- Antal anställda -->
      <v-text-field
        v-model="company.employees"
        :label="t('company.employees')"
        color="primary"
      />

      <!-- Om företaget (sv/en) -->
      <v-textarea
        v-model="company.about.sv"
        :label="t('company.about') + ' (sv)'"
        color="primary"
      />
      <v-textarea
        v-model="company.about.en"
        :label="t('company.about') + ' (en)'"
        color="primary"
      />

      <!-- Värderingar -->
      <v-textarea
        v-model="company.values.sv"
        :label="t('company.values') + ' (sv)'"
        color="primary"
      />
      <v-textarea
        v-model="company.values.en"
        :label="t('company.values') + ' (en)'"
        color="primary"
      />

      <!-- Platser -->
      <v-textarea
        v-model="company.locations.sv"
        :label="t('company.locations') + ' (sv)'"
        color="primary"
      />
      <v-textarea
        v-model="company.locations.en"
        :label="t('company.locations') + ' (en)'"
        color="primary"
      />

      <!-- Taggar -->
      <v-combobox
        v-model="company.tags.sv"
        :label="t('company.tags') + ' (sv)'"
        multiple
        chips
        deletable-chips
        small-chips
        color="primary"
      />

      <v-combobox
        v-model="company.tags.en"
        :label="t('company.tags') + ' (en)'"
        multiple
        chips
        deletable-chips
        small-chips
        color="primary"
      />

      <!-- Partnerstatus -->
      <v-checkbox v-model="company.partner" label="Partner" color="primary" />

      <!-- Kontaktmöten -->
      <v-checkbox
        v-model="company.contactMeeting"
        :label="t('company.contactMeeting')"
        color="primary"
      />

      <!-- Tillgängliga tider -->
      <v-combobox
        v-if="company.contactMeeting"
        v-model="company.availableTimes"
        :items="availableTimesEnum"
        :label="t('company.availableTimes')"
        multiple
        chips
        deletable-chips
        small-chips
        color="primary"
      />

      <v-btn type="submit">{{ t("company.addCompany") }}</v-btn>
    </v-form>
  </v-container>
</template>

<style scoped>
.v-btn {
  transition: all 0.3s ease;
}

.v-btn:hover {
  background-color: #763493 !important;
  color: white !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
